import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { Field, RadioCard } from '@noorden-insurtech/alba-design-system';
import type { ReactNode } from 'react';
import type { TFieldContext } from '../types';

export type RadioCardOption = {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
};

export type RadioCardFieldProps = {
  label?: string;
  options: RadioCardOption[];
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
  direction?: 'row' | 'column';
  /**
   * Sobrescreve o valor exibido pelo grupo, ignorando `field.state.value`.
   * Útil quando o campo está desabilitado e precisa refletir de forma
   * confiável um valor conhecido fora do ciclo de preenchimento assíncrono
   * do form (ex.: prefill de edição) — evita depender do timing de um
   * `setFieldValue` disparado em efeito.
   */
  value?: string;
};

export function createRadioCardField(useFieldContext: () => unknown) {
  return function RadioCardField({
    label,
    options,
    required,
    helperText,
    disabled,
    direction = 'row',
    value,
  }: RadioCardFieldProps) {
    const field = useFieldContext() as TFieldContext<string>;
    const hasError = field.state.meta.errors.length > 0;
    const displayValue = value ?? field.state.value ?? '';

    return (
      <RequiredFieldTooltip active={required}>
        <Field.Root invalid={hasError}>
          {label && (
            <Field.Label>
              {label}
              {required && <span className='text-alba-error-red-500'>*</span>}
            </Field.Label>
          )}
          <RadioCard.Root
            // `displayValue` honra a prop `value` (passo Cliente do RD) e cai em
            // `field.state.value` quando ela não vem — igual a antes para os demais.
            value={displayValue}
            onValueChange={details => field.handleChange(details.value || '')}
            disabled={disabled}
            className={direction === 'row' ? 'flex-row flex-wrap' : undefined}
          >
            {options.map(option => (
              <RadioCard.Item key={option.value} value={option.value}>
                <RadioCard.ItemControl>
                  <RadioCard.ItemContent>
                    {option.icon && (
                      <RadioCard.ItemIcon className='mb-2'>
                        {option.icon}
                      </RadioCard.ItemIcon>
                    )}
                    <RadioCard.ItemText>{option.label}</RadioCard.ItemText>
                    {option.description && (
                      <RadioCard.ItemDescription className='mt-1'>
                        {option.description}
                      </RadioCard.ItemDescription>
                    )}
                  </RadioCard.ItemContent>
                  <RadioCard.RadioButton />
                </RadioCard.ItemControl>
                <RadioCard.ItemHiddenInput />
              </RadioCard.Item>
            ))}
          </RadioCard.Root>
          {helperText && !hasError && (
            <Field.HelperText>{helperText}</Field.HelperText>
          )}
          {hasError && (
            <Field.ErrorText>{field.state.meta.errors[0]}</Field.ErrorText>
          )}
        </Field.Root>
      </RequiredFieldTooltip>
    );
  };
}
